import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formService } from '../../../services/formServices'; // Adjust path as needed

import './Modal.scss';

const JobApplyModal = ({ onClose, jobId, jobPosition = "Embedded Engineer" }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        resumeUrl: '', // Will be set after upload
        job: jobId || '' // Use job ID
    });

    const [resumeFile, setResumeFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file
        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
            alert('Please upload a PDF, DOC, or DOCX file');
            return;
        }

        if (file.size > maxSize) {
            alert('File size must be less than 5MB');
            return;
        }

        setResumeFile(file);

        try {
            // Upload file to get URL
            setIsSubmitting(true);
            setUploadProgress(30);

            // In a real app, you would upload to S3/Cloud Storage
            // For now, we'll simulate upload and use a placeholder
            // const uploadResponse = await formService.uploadResume(file);

            // Simulate upload delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            // In production, use the actual URL from upload response
            const mockResumeUrl = `https://storage.example.com/resumes/${Date.now()}_${file.name}`;
            setFormData(prev => ({ ...prev, resumeUrl: mockResumeUrl }));

            setUploadProgress(100);
        } catch (error) {
            console.error('File upload error:', error);
            alert('Failed to upload resume. Please try again.');
            setResumeFile(null);
        } finally {
            setIsSubmitting(false);
            setUploadProgress(0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!jobId) {
            alert('Job ID is required');
            return;
        }

        if (!resumeFile || !formData.resumeUrl) {
            alert('Please upload your resume');
            return;
        }

        setIsSubmitting(true);

        try {
            // Submit application
            await formService.submitCareerApplication({
                ...formData,
                job: jobId
            });

            // Success
            alert("Application submitted successfully!");

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: '',
                resumeUrl: '',
                job: jobId
            });
            setResumeFile(null);

            onClose();
        } catch (error) {
            console.error('Application submission error:', error);
            alert(error.response?.data?.message || "Failed to submit application. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-modal">
            <div className="modal-grid">
                {/* Left Column */}
                <div className="modal-left">
                    <motion.span
                        className="support-label"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        CAREER OPPORTUNITY
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Join Our Team
                    </motion.h2>

                    <motion.p
                        className="subheading"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        {jobPosition ? `Applying for: ${jobPosition}` : "We're excited to review your application!"}
                    </motion.p>

                    <motion.div
                        className="application-tips"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <h4>Application Requirements:</h4>
                        <ul>
                            <li>✓ Resume (PDF, DOC, DOCX) - Max 5MB</li>
                            <li>✓ Valid email address</li>
                            <li>✓ Phone number for contact</li>
                        </ul>
                    </motion.div>
                </div>

                {/* Right Column - Form */}
                <motion.div
                    className="modal-right"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <form onSubmit={handleSubmit} className="contact-form">
                        <div className="form-row">
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Full Name *"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email Address *"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number *"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label className="file-input-label">
                                Upload Resume (PDF, DOC, DOCX) *
                                {uploadProgress > 0 && (
                                    <span className="upload-progress">
                                        Uploading... {uploadProgress}%
                                    </span>
                                )}
                            </label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,.doc,.docx"
                                required
                                className="form-input"
                                disabled={isSubmitting}
                            />
                            {resumeFile && (
                                <div className="file-info">
                                    Selected: {resumeFile.name}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <textarea
                                name="message"
                                placeholder="Additional Message (Optional)"
                                rows="3"
                                value={formData.message}
                                onChange={handleInputChange}
                                className="form-textarea"
                                disabled={isSubmitting}
                            />
                        </div>

                        <motion.button
                            type="submit"
                            className="send-btn"
                            disabled={isSubmitting || !resumeFile}
                            whileHover={(!isSubmitting && resumeFile) ? { scale: 1.02 } : {}}
                            whileTap={(!isSubmitting && resumeFile) ? { scale: 0.98 } : {}}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </motion.button>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default JobApplyModal;